import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRegistry } from '../context/RegistryContext';
import { Search, AlertCircle } from 'lucide-react';

const Lookup = () => {
  const navigate = useNavigate();
  const { getServiceDogByRegistryNumber } = useRegistry();

  const [registryNumber, setRegistryNumber] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();

    if (!registryNumber.trim()) {
      setError('Please enter a registry number');
      return;
    }

    setLoading(true);

    try {
      // Look up the service dog in the registry
      const dog = await getServiceDogByRegistryNumber(registryNumber.trim());

      if (dog) {
        // Redirect to the registry card page
        navigate(`/registry/${dog.id}`);
      } else {
        setError('No service dog found with this registry number');
      }
    } catch (error) {
      console.error('Error looking up service dog:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-primary-600 text-white p-4 sm:p-6">
          <h1 className="text-xl sm:text-2xl font-bold">Verify a Service Dog Registration</h1>
          <p className="text-primary-100 mt-1 sm:mt-2 text-sm sm:text-base">
            Enter a service dog registry number to verify its registration status.
          </p>
        </div>

        <div className="p-4 sm:p-6">
          {error && (
            <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-accent-100 text-accent-700 rounded-lg flex items-start text-sm">
              <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSearch} className="mb-4 sm:mb-8">
            <div className="mb-4 sm:mb-6">
              <label htmlFor="registryNumber" className="block text-sm font-medium text-neutral-700 mb-1">
                Registry Number <span className="text-accent-600">*</span>
              </label>
              <div className="flex flex-col sm:flex-row w-full">
                <input
                  type="text"
                  id="registryNumber"
                  placeholder="e.g., NJ-DOJ-123456"
                  value={registryNumber}
                  onChange={(e) => {
                    setRegistryNumber(e.target.value);
                    if (error) setError('');
                  }}
                  className="w-full px-4 py-2 sm:py-3 border border-neutral-300 rounded-lg sm:rounded-r-none sm:rounded-l-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto mt-2 sm:mt-0 bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-l-none sm:rounded-r-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <Search size={18} className="mr-2" />
                  <span>Search</span>
                </button>
              </div>
              <p className="mt-2 text-xs sm:text-sm text-neutral-600">
                Enter the registry number provided to the service dog owner. Format: SD-XXXXXX
              </p>
            </div>
          </form>

          <div className="bg-neutral-50 p-3 sm:p-6 rounded-lg border border-neutral-200 text-sm sm:text-base">
            <h2 className="text-base sm:text-lg font-semibold text-primary-700 mb-2 sm:mb-4">About Service Dog Verification</h2>
            <p className="text-neutral-600 mb-3 sm:mb-4 text-sm">
              The US Service Dog Registry provides a way to verify that a service dog has been properly registered and meets the necessary qualifications.
            </p>
            <ul className="text-neutral-600 space-y-1 sm:space-y-2 text-sm">
              <li className="flex items-start">
                <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-primary-600 mr-2 mt-1 flex-shrink-0"></span>
                <span>Business owners can verify service dogs to ensure compliance with accessibility laws.</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-primary-600 mr-2 mt-1 flex-shrink-0"></span>
                <span>Public facilities can confirm the legitimacy of service animals on their premises.</span>
              </li>
              <li className="flex items-start">
                <span className="inline-block w-3 h-3 sm:w-4 sm:h-4 rounded-full bg-primary-600 mr-2 mt-1 flex-shrink-0"></span>
                <span>Service dog owners can provide verification when requested in appropriate situations.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Lookup;